import moment from 'moment-timezone';
import { applyLocaleTimeConventions, formatTimeOfDay, resolvePresentationLocale } from '@hqo/date-time/core';

type TimeInDayInMinutes = number;

export function parseTime(timeString: string): TimeInDayInMinutes {
  const [hours, minutes] = timeString.split(':').map(Number);

  if (hours === undefined || minutes === undefined || Number.isNaN(hours) || Number.isNaN(minutes)) {
    throw new Error(`Invalid time string "${timeString}" should match HH:mm format`);
  }

  return hours * 60 + minutes;
}

export enum TimeStringFormats {
  HOURS_ONLY_UP = 'HOURS_ONLY_UP',
  HH_MM_UP = 'HH_MM_UP',
}

export function formatTime(
  time: TimeInDayInMinutes,
  locale: string,
  format: TimeStringFormats = TimeStringFormats.HH_MM_UP,
): string {
  const date = moment().startOf('day').set('minute', time).toDate();

  if (format === TimeStringFormats.HOURS_ONLY_UP) {
    // Timeline axis tick — hour only (the one place hour-only is defensible, since the axis supplies
    // the "this is a time" context). Resolve the presentation locale FIRST so a tag Intl would refuse
    // cannot reach DateTimeFormat and throw RangeError, then reuse it for both the formatter (locale
    // LIST guards the host fallback) and the French `h` override.
    //
    // It guards the THROW, not a wrong answer: given `en_GB.UTF-8` it returns `en-US`, silently losing
    // the region. `useLocale` normalizes before anything reaches here, which is why that shape no
    // longer arrives — do not rely on this call to do it.
    const presentationLocale = resolvePresentationLocale(locale);
    const hour = new Intl.DateTimeFormat([presentationLocale, 'en-US'], { hour: 'numeric' }).format(date);
    return applyLocaleTimeConventions(hour, presentationLocale);
  }

  // HH_MM_UP — full localized time-of-day. CLDR owns padding and the separator, so France reads
  // `10 h 00` (not the old `10 H 00` an uppercase pass produced) and each locale pads as it should.
  return formatTimeOfDay(date, { locale });
}

/**
 * Locale-INDEPENDENT 24-hour `HH:mm` encoding of a minute-of-day. Not for display — this is an internal
 * handle (slot keys, availability endpoints) that must stay stable regardless of the user's locale.
 * Wraps past midnight (`24:45` minutes -> `00:45`).
 */
export function formatTime24(time: TimeInDayInMinutes): string {
  const normalized = ((Math.round(time) % 1440) + 1440) % 1440;
  const hours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const minutes = String(normalized % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function getTimeInDayInMinutesFromUTCISO8601(datetime: string, timezone: string): TimeInDayInMinutes {
  const m = moment(datetime).tz(timezone);
  return m.hours() * 60 + m.minutes();
}
